import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Separator } from '@/shared/components/ui/separator';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { useToast } from '@/shared/hooks/use-toast';
import { resolveApiErrorMessage } from '@/shared/utils/apiErrorMessage';
import { Mail, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { DynamicFieldRenderer } from '@/shared/components/form/DynamicFieldRenderer';
import { getLocalizedText } from '@/shared/utils/i18nHelper';
import { EmailServiceService } from '../services/emailServiceService';
import type { EmailServiceConfig, EmailFieldsSchema, EmailProviderDef } from '../types/EmailServiceConfigInterface';
import type { EmailProvider } from '../types/EmailProviderType';
import type { UpdateEmailServicePayload } from '../types/UpdateEmailServicePayload';

export const EmailServiceSettings = () => {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const currentLang = i18n.language.split('-')[0];

  // Form state as a flat object with dotted keys (e.g. "smtp.host")
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [provider, setProvider] = useState<EmailProvider>('smtp');
  const [testEmail, setTestEmail] = useState('');

  // Load config from API
  const { data: config, isLoading: isLoadingConfig, error: configError } = useQuery({
    queryKey: ['email-service-config'],
    queryFn: () => EmailServiceService.getConfig(),
    retry: 2,
  });

  // Extract fields schema
  const fieldsSchema: EmailFieldsSchema | undefined = config?.fields;

  // Populate form when config loads
  const populateForm = useCallback((data: EmailServiceConfig) => {
    const p = data.provider === 'nodemailer' ? 'smtp' : data.provider;
    setProvider(p);

    const dataRecord = data as EmailServiceConfig & Record<string, unknown>;
    const values: Record<string, unknown> = {
      fromEmail: data.fromEmail || '',
      organizationName: data.organizationName || '',
    };

    // Populate dynamic common_fields (e.g. fromName)
    if (data.fields?.common_fields) {
      for (const field of data.fields.common_fields) {
        if (!(field.id in values)) {
          values[field.id] = dataRecord[field.id] ?? '';
        }
      }
    }

    // SMTP
    if (data.smtp) {
      values['smtp.host'] = data.smtp.host || '';
      values['smtp.port'] = data.smtp.port || 587;
      values['smtp.secure'] = data.smtp.secure || false;
      values['smtp.user'] = data.smtp.user || '';
      values['smtp.password'] = data.smtp.password || '';
    }

    // Microsoft 365
    if (data.microsoft365) {
      values['microsoft365.tenantId'] = data.microsoft365.tenantId || '';
      values['microsoft365.clientId'] = data.microsoft365.clientId || '';
      values['microsoft365.clientSecret'] = data.microsoft365.clientSecret || '';
      values['microsoft365.mailbox'] = data.microsoft365.mailbox || '';
    }

    // Google
    if (data.google) {
      values['google.serviceAccountKey'] = data.google.serviceAccountKey || '';
      values['google.mailbox'] = data.google.mailbox || '';
    }

    setFormValues(values);
  }, []);

  useEffect(() => {
    if (config && Object.keys(formValues).length === 0) {
      populateForm(config);
    }
  }, [config, formValues, populateForm]);

  // Build update payload from flat form values
  const buildPayload = () => {
    const isMasked = (v: string | undefined) => v === '******' || v === '';

    const payload: Record<string, unknown> = {
      provider,
      fromEmail: formValues.fromEmail,
      organizationName: formValues.organizationName,
      default_provider: true,
    };

    // Include dynamic common_fields (e.g. fromName)
    if (fieldsSchema?.common_fields) {
      const knownKeys = new Set(['fromEmail', 'organizationName']);
      for (const field of fieldsSchema.common_fields) {
        if (!knownKeys.has(field.id)) {
          payload[field.id] = formValues[field.id] ?? '';
        }
      }
    }

    if (provider === 'smtp') {
      payload.smtp = {
        service: 'custom',
        host: formValues['smtp.host'] || '',
        port: Number(formValues['smtp.port']) || 587,
        secure: !!formValues['smtp.secure'],
        user: formValues['smtp.user'] || '',
        password: isMasked(formValues['smtp.password']) ? undefined : formValues['smtp.password'],
      };
    } else if (provider === 'microsoft365') {
      payload.microsoft365 = {
        tenantId: formValues['microsoft365.tenantId'] || '',
        clientId: formValues['microsoft365.clientId'] || '',
        clientSecret: isMasked(formValues['microsoft365.clientSecret']) ? undefined : formValues['microsoft365.clientSecret'],
        mailbox: formValues['microsoft365.mailbox'] || '',
      };
    } else if (provider === 'google') {
      payload.google = {
        serviceAccountKey: isMasked(formValues['google.serviceAccountKey']) ? undefined : formValues['google.serviceAccountKey'],
        mailbox: formValues['google.mailbox'] || '',
      };
    }

    return payload as unknown as UpdateEmailServicePayload;
  };

  // Save mutation
  const updateMutation = useMutation({
    mutationFn: async () => EmailServiceService.updateConfig(buildPayload()),
    onSuccess: (data) => {
      toast({
        title: t('settings.emailService.saveSuccess'),
        description: data.message,
      });
      queryClient.invalidateQueries({ queryKey: ['email-service-config'] });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: t('settings.emailService.saveError'),
        description: resolveApiErrorMessage(error, t),
      });
    },
  });

  // Test mutation
  const testMutation = useMutation({
    mutationFn: async () => {
      if (!testEmail) {
        throw new Error(t('settings.emailService.testRequired'));
      }
      return EmailServiceService.testConfig({ ...buildPayload(), testEmail });
    },
    onSuccess: (data) => {
      toast({
        title: t('settings.emailService.testSuccess'),
        description: data.message,
        action: <CheckCircle className="h-4 w-4" />,
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: t('settings.emailService.testFailed'),
        description: resolveApiErrorMessage(error, t),
        action: <AlertCircle className="h-4 w-4" />,
      });
    },
  });

  // Handle dynamic field change
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFieldChange = (fieldId: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  // Get current provider definition from schema
  const currentProviderDef: EmailProviderDef | undefined = fieldsSchema?.providers?.find(
    (p) => p.id === provider
  );

  if (isLoadingConfig) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="ml-2">{t('settings.emailService.loading')}</span>
        </CardContent>
      </Card>
    );
  }

  if (configError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {t('settings.emailService.loadError')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            {t('settings.emailService.title')}
          </CardTitle>
          <CardDescription>
            {t('settings.emailService.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Provider Selector */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              {t('settings.emailService.sendingMethod')}
            </h3>

            <div className="space-y-2">
              <Label htmlFor="provider">
                {t('settings.emailService.emailProtocol')}
              </Label>
              <Select value={provider} onValueChange={(value) => setProvider(value as EmailProvider)}>
                <SelectTrigger id="provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {fieldsSchema?.providers ? (
                    fieldsSchema.providers.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {getLocalizedText(p.label, currentLang)}
                      </SelectItem>
                    ))
                  ) : (
                    <>
                      <SelectItem value="smtp">SMTP</SelectItem>
                      <SelectItem value="microsoft365">Microsoft 365</SelectItem>
                      <SelectItem value="google">Google Workspace</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
              {currentProviderDef?.description && (
                <p className="text-xs text-muted-foreground">
                  {getLocalizedText(currentProviderDef.description, currentLang)}
                </p>
              )}
            </div>
          </div>

          <Separator />

          {/* Common Fields */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              {t('settings.emailService.generalSettings')}
            </h3>

            {fieldsSchema?.common_fields ? (
              fieldsSchema.common_fields.map((field) => (
                <DynamicFieldRenderer
                  key={field.id}
                  field={{
                    id: field.id,
                    type: field.type as 'text' | 'email',
                    label: field.label,
                    placeholder: field.placeholder,
                    description: field.description,
                    required: field.required,
                  }}
                  value={formValues[field.id]}
                  onChange={handleFieldChange}
                />
              ))
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="fromEmail">{t('settings.emailService.fromEmail')}</Label>
                  <Input
                    id="fromEmail"
                    type="email"
                    placeholder="no-reply@prodgy.ai"
                    value={formValues.fromEmail || ''}
                    onChange={(e) => handleFieldChange('fromEmail', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="organizationName">{t('settings.emailService.organizationName')}</Label>
                  <Input
                    id="organizationName"
                    placeholder="Prodgy"
                    value={formValues.organizationName || ''}
                    onChange={(e) => handleFieldChange('organizationName', e.target.value)}
                  />
                </div>
              </>
            )}
          </div>

          <Separator />

          {/* Provider-Specific Fields (Dynamic) */}
          {currentProviderDef && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">
                {getLocalizedText(currentProviderDef.label, currentLang)}
              </h3>

              {currentProviderDef.fields.map((field) => (
                <DynamicFieldRenderer
                  key={field.id}
                  field={{
                    id: field.id,
                    type: field.type as 'text' | 'number' | 'password' | 'email' | 'textarea' | 'switch',
                    label: field.label,
                    placeholder: field.placeholder,
                    description: field.description,
                    required: field.required,
                  }}
                  value={formValues[field.id]}
                  onChange={handleFieldChange}
                />
              ))}
            </div>
          )}

          {/* Fallback for when schema is not loaded */}
          {!fieldsSchema && provider === 'smtp' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">{t('settings.emailService.smtpSection')}</h3>
              <div className="space-y-2">
                <Label>{t('settings.emailService.smtpHost')}</Label>
                <Input
                  placeholder="smtp.office365.com"
                  value={formValues['smtp.host'] || ''}
                  onChange={(e) => handleFieldChange('smtp.host', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('settings.emailService.smtpPort')}</Label>
                <Input
                  type="number"
                  placeholder="587"
                  value={formValues['smtp.port'] || 587}
                  onChange={(e) => handleFieldChange('smtp.port', Number(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('settings.emailService.smtpUser')}</Label>
                <Input
                  value={formValues['smtp.user'] || ''}
                  onChange={(e) => handleFieldChange('smtp.user', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('settings.emailService.smtpPassword')}</Label>
                <Input
                  type="password"
                  value={formValues['smtp.password'] || ''}
                  onChange={(e) => handleFieldChange('smtp.password', e.target.value)}
                />
              </div>
            </div>
          )}

          <Separator />

          {/* Test Connection */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              {t('settings.emailService.testConfiguration')}
            </h3>

            <div className="space-y-2">
              <Label htmlFor="testEmail">
                {t('settings.emailService.testEmail')}
              </Label>
              <Input
                id="testEmail"
                type="email"
                placeholder={t('settings.emailService.testEmailPlaceholder')}
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t('settings.emailService.testEmailHint')}
              </p>
            </div>

            <Button
              variant="outline"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending || !testEmail}
            >
              {testMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('settings.emailService.testConnection')}
            </Button>
          </div>

          <Separator />

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              onClick={() => updateMutation.mutate()}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('settings.emailService.saveConfiguration')}
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                if (config) {
                  setFormValues({});
                  setTimeout(() => populateForm(config), 0);
                }
              }}
              disabled={updateMutation.isPending}
            >
              {t('settings.emailService.reset')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
